import React from "react";
import { Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";

/** What Tessera is (and isn't) for deployed strategies — shown before trading on a broker account
 *  and, short, on the page. Users build, configure and deploy their own strategies. */
export const DISCLAIMER_POINTS: string[] = [
  "Your strategy, your settings, your trades. You build, configure, deploy and stop your strategies yourself; Tessera runs them exactly as you set them up. We don't design, review, select, recommend or manage strategies for you.",
  "Not advice. Nothing in Tessera (backtests, reports, TES, signals or statistics) is investment, financial or trading advice, or a recommendation to buy or sell anything.",
  "Trading is risky. Leveraged products such as CFDs and FX can lose money fast, and you can lose more than you put in. Only trade money you can afford to lose.",
  "Past results aren't future results. Backtests and paper trading are simulations: live fills, spreads, slippage and data can differ, and a strategy that tested well can lose.",
  "Things can fail. Market data can be late or missing, connections and brokers can refuse or delay orders, and software can have faults. Your broker holds each trade's stop and target, but you are responsible for watching your account and positions.",
  "You're responsible for every order placed on your account by your strategies, and for checking they suit you and are allowed where you live.",
];

export function DeployDisclaimer({ compact }: { compact?: boolean }): JSX.Element {
  if (compact) {
    return (
      // The app's disclaimer row (research disclaimer): full width, info icon, grey body.
      <Alert
        variant="info"
        className="flex w-full items-start gap-3 rounded-lg px-4 py-3 font-[family-name:var(--font-primary)]"
        data-live-disclaimer=""
      >
        <span aria-hidden className="flex h-5 w-4 shrink-0 items-center justify-center">
          <Info className="size-4 text-[color:var(--brand-tropical-surf)]" />
        </span>
        <AlertDescription className="min-w-0 flex-1 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 !text-gray-500 dark:!text-white">
          Strategies are built, configured and deployed by you. Tessera runs them as you set them up and doesn&apos;t
          give investment advice. Trading leveraged products is high risk; backtests and paper results don&apos;t
          guarantee live results. You&apos;re responsible for the trades on your account.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <ul className="!m-0 flex !list-disc flex-col gap-2 !pl-4 font-[family-name:var(--font-primary)] text-xs leading-relaxed text-gray-500 dark:text-white" data-live-disclaimer="">
      {DISCLAIMER_POINTS.map((p) => {
        const [lead, ...rest] = p.split(". ");
        return (
          <li key={p}>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">{lead}.</span> {rest.join(". ")}
          </li>
        );
      })}
    </ul>
  );
}
